// src/components/ui/AlertStrip.tsx
// The one rendering of a clinical alert, promoted out of
// features/ip-case-sheet/AlertStrip.tsx — the Consultation wireframe's
// marker 2, and the moment three near-identical copies of this row became
// one. Before this there were: the IP Case Sheet's own AlertStrip, the OP
// prescribing/dispensing SafetyAlertBanner, and the 12px allergy line beside
// the Consultation tabs. The first two are now thin adapters over this file;
// the third is gone.
//
// ── The two rules this component exists to hold ──────────────────
//
// 1. A RED alert is never collapsed behind a "show more". Amber and info
//    collapse past the first few, because a strip that fills the screen is
//    one clinicians learn to scroll past.
// 2. Every alert carries a "Why?" that states the basis it was computed
//    from. A statement a clinician cannot check the provenance of is one
//    they should not act on.
//
// What it must never do: none of these alerts are AI output. Callers pass
// alerts derived deterministically from rows (backend/utils/ipSafetyRules.js,
// backend/utils/rxSafetyChecks.js, or the patient's own allergy record) and
// nothing a model says can add, remove or re-rank one. Nor may a caller wire
// `alerts.length > 0` into a `disabled` prop — these inform a decision, they
// never make it.
import { useState, type ReactNode } from 'react';
import { Button } from './Button';
import { cn } from '@/lib/cn';

export type AlertSeverity = 'red' | 'amber' | 'info';

const SEVERITY_STYLE: Record<AlertSeverity, string> = {
  red: 'border-danger/40 bg-danger-soft text-danger-ink',
  amber: 'border-warn/40 bg-warn-soft text-warn-ink',
  info: 'border-info/40 bg-info-soft text-info-ink',
};

const SEVERITY_LABEL: Record<AlertSeverity, string> = {
  red: 'Critical', amber: 'Review', info: 'Info',
};

export function severityClass(s: AlertSeverity): string {
  return SEVERITY_STYLE[s] ?? SEVERITY_STYLE.info;
}

export interface StripAlert {
  /** Stable React key. Callers build it from whatever identifies the alert in their own domain (a rule key, a row id). */
  key: string;
  label: string;
  severity: AlertSeverity;
  message: string;
  detail?: string | null;
  /** What the alert was computed from — a published threshold, a recorded row. Omit and no "Why?" affordance renders at all, which is the honest outcome for an alert with no stated basis. */
  basis?: string | null;
  /** A short qualifier printed after the basis, e.g. "rule v2". */
  basisMeta?: string | null;
  /** Replaces the default advisory sentence under the basis — each surface states what it does and does not block. */
  advisoryNote?: string;
  /** Extra affordance in the alert's own action row, e.g. a link to the source record. */
  action?: ReactNode;
}

const DEFAULT_ADVISORY = 'Advisory only — computed from the record, not a clinical decision.';

function AlertRow({ alert }: { alert: StripAlert }) {
  const [showBasis, setShowBasis] = useState(false);
  return (
    <div className={cn('rounded-lg border px-3 py-2', severityClass(alert.severity))}>
      <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1">
        <div className="min-w-0">
          <div className="text-[10px] font-bold uppercase tracking-wider opacity-80">
            {SEVERITY_LABEL[alert.severity] ?? 'Info'} · {alert.label}
          </div>
          <div className="mt-0.5 text-[13px] font-medium">{alert.message}</div>
          {alert.detail && <div className="mt-0.5 text-[11.5px] opacity-90">{alert.detail}</div>}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {alert.action}
          {alert.basis && (
            <button
              type="button"
              onClick={() => setShowBasis(v => !v)}
              className="text-[11px] underline-offset-2 hover:underline opacity-80"
            >
              Why?
            </button>
          )}
        </div>
      </div>
      {showBasis && alert.basis && (
        <div className="mt-2 rounded border border-current/20 bg-card/60 px-2 py-1.5 text-[11px] opacity-90">
          <span className="font-semibold">Basis: </span>{alert.basis}
          {alert.basisMeta && <span className="ml-1 opacity-70">({alert.basisMeta})</span>}
          <div className="mt-1 opacity-70">{alert.advisoryNote ?? DEFAULT_ADVISORY}</div>
        </div>
      )}
    </div>
  );
}

export interface AlertStripProps {
  alerts: StripAlert[];
  /** How many non-red alerts stay visible before the rest collapse behind a "show more". `false` shows every alert — for a surface that already renders a short, bounded list (prescribing checks against one drug set). Red is never affected either way. */
  collapseAfter?: number | false;
  className?: string;
}

export function AlertStrip({ alerts, collapseAfter = 2, className }: AlertStripProps) {
  const [expanded, setExpanded] = useState(false);
  if (!alerts.length) return null;

  const red = alerts.filter(a => a.severity === 'red');
  const rest = alerts.filter(a => a.severity !== 'red');
  const visibleRest = collapseAfter === false || expanded ? rest : rest.slice(0, collapseAfter);
  const hidden = rest.length - visibleRest.length;

  return (
    <div className={cn('space-y-1.5', className)}>
      {red.map(a => <AlertRow key={a.key} alert={a} />)}
      {visibleRest.map(a => <AlertRow key={a.key} alert={a} />)}
      {hidden > 0 && (
        <Button variant="ghost" size="sm" onClick={() => setExpanded(true)}>
          Show {hidden} more alert{hidden > 1 ? 's' : ''}
        </Button>
      )}
      {collapseAfter !== false && expanded && rest.length > collapseAfter && (
        <Button variant="ghost" size="sm" onClick={() => setExpanded(false)}>Show fewer</Button>
      )}
    </div>
  );
}
